/**
 * GroupsList Selectors
 */
import { createSelectorCreator, defaultMemoize } from 'reselect';
import isEqual from 'lodash.isequal';
import search from 'javascript-array-search';

// Selectors
import { selectGroups } from '../../../../../../services/groups/selectors';
import { selectCurrentEntityGroupIds } from '../../../../../../services/groupEntities/selectors';

// Workspaces
import {
    makeSelectQueryStringValue,
    makeSelectQueryStringNumberValue
} from '../../../../../../workspaces/react-router-selectors';

// Utils
import orderBy from '../../../../../../utils/custom-orderBy';

const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

const selectCurrentEntityGroups = createDeepEqualSelector(
    [selectCurrentEntityGroupIds, selectGroups],
    (currentEntityGroupIds, groupsState) => {
        if (!currentEntityGroupIds) return groupsState;
        return currentEntityGroupIds
            .map(value => groupsState[value])
            .filter(value => value);
    }
);

const selectData = createDeepEqualSelector(
    selectCurrentEntityGroups,
    currentEntityGroupsState => Object.values(currentEntityGroupsState)
);

const selectFilteredData = createDeepEqualSelector(
    [selectData, makeSelectQueryStringValue('search')],
    (dataState, searchState) => search(dataState, searchState, ['name'])
);

const selectDataLength = createDeepEqualSelector(
    selectFilteredData,
    filteredDataState => filteredDataState.length
);

const selectOrderedData = createDeepEqualSelector(
    [selectFilteredData, makeSelectQueryStringValue('sort')],
    (filteredDataState, sortState = 'name_ascending') => {
        const attributes = sortState.split('_');
        const propertyName = attributes[0];
        const order = attributes[1] !== 'descending' ? 'asc' : 'desc';

        return orderBy(filteredDataState, propertyName, order);
    }
);

const selectPaginatedData = createDeepEqualSelector(
    [
        selectOrderedData,
        makeSelectQueryStringNumberValue('page'),
        makeSelectQueryStringNumberValue('count')
    ],
    (orderedDataState, pageState = 1, countState = 25) => {
        const begin = (pageState - 1) * countState;
        const end = pageState * countState;
        return orderedDataState.slice(begin, end);
    }
);

const selectIds = createDeepEqualSelector(
    selectPaginatedData,
    paginatedDataState =>
        paginatedDataState && paginatedDataState.map(value => value.id)
);

export {
    selectData,
    selectDataLength,
    selectFilteredData,
    selectOrderedData,
    selectIds
};
